import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` });

const card = { border:'1px solid #e5e7eb', borderRadius:12, background:'#fff', padding:14 };
const row  = { display:'grid', gridTemplateColumns:'160px 1fr', gap:10, margin:'6px 0' };
const k    = { color:'#6b7280' };
const v    = { color:'#111827' };

function prettyDT(v){
    if (!v) return '—';
    try {
        return new Intl.DateTimeFormat('pl-PL',{dateStyle:'medium', timeStyle:'short'}).format(new Date(v));
    } catch { return v; }
}
function pct(points, max){
    const p = Number(points), m = Number(max);
    if (!Number.isFinite(p) || !Number.isFinite(m) || m <= 0) return null;
    return Math.round((p/m)*100);
}
function statusLabel(s){
    switch(s){
        case 'pending_verification':      return 'Weryfikacja';
        case 'in_progress':      return 'W trakcie';
        case 'submitted':        return 'Złożony';
        case 'awaiting_manual':  return 'Czeka na ocenę';
        case 'auto_graded':      return 'Oceniony automatycznie';
        case 'completed':        return 'Zakończony';
        case 'canceled':         return 'Unieważniony';
        default:                 return s || '—';
    }
}
function clientBadge(client, msg){
    if (client === 'seb') return <span style={{ color:'#059669', fontWeight:700 }}>SEB</span>;
    return <span style={{ color:'#dc2626' }}>Przeglądarka {msg ? <span style={{ opacity:.7 }}>({msg})</span> : null}</span>;
}
function verificationText(st){
    switch(st){
        case 'auto_pass':        return { text:'Weryfikacja automatyczna — poprawna', color:'#059669' };
        case 'manual_approved':  return { text:'Weryfikacja ręczna — zatwierdzona',   color:'#059669' };
        case 'manual_pending':   return { text:'Wymaga weryfikacji ręcznej',          color:'#b45309' };
        case 'manual_rejected':  return { text:'Weryfikacja — niezgodna',             color:'#dc2626' };
        case 'not_required':     return { text:'Weryfikacja niewymagana',             color:'#6b7280' };
        default:                 return { text: st || '—',                            color:'#6b7280' };
    }
}
function proctorColor(score){
    const v = Number(score);
    if (!Number.isFinite(v)) return '#9ca3af';
    if (v < 20) return '#10b981';
    if (v <= 100) return '#f59e0b';
    return '#ef4444';
}

export default function AttemptDetails(){
    const { id } = useParams(); // attempt_id
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');
    const [data, setData] = useState(null); // { attempt, user, verification }
    const [verifyOpen, setVerifyOpen] = useState(false);

    // Dodaj czas
    const [extraMin, setExtraMin] = useState('');
    const [extraOk, setExtraOk] = useState('');
    const [extraErr, setExtraErr] = useState('');

    // Notatka indywidualna
    const [attNoteVal, setAttNoteVal] = useState('');
    const [attNoteMsg, setAttNoteMsg] = useState('');
    const [savingAttNote, setSavingAttNote] = useState(false);

    const fetchData = async () => {
        try {
            setErr('');
            const r = await fetch(`/api/prowadzacy/attempt/${id}`, {
                credentials: 'include',
                headers: { ...authHeader() }
            });
            if (!r.ok){
                const j = await r.json().catch(()=> ({}));
                throw new Error(j?.blad || `Błąd pobierania (${r.status})`);
            }
            const j = await r.json();
            setData(j);
            const noteFromApi = j?.attempt?.attempt_note ?? j?.attempt?.note;
            if (noteFromApi !== undefined && noteFromApi !== null) {
                   setAttNoteVal(String(noteFromApi));
                 }
        } catch(e){
            setErr(e.message || 'Błąd pobierania');
        } finally {
            setLoading(false);
        }
    };

    useEffect(()=>{ fetchData(); /* eslint-disable-next-line */ }, [id]);

    const att = data?.attempt;
    const usr = data?.user;
    const ver = data?.verification;

    const pointsPct = useMemo(()=> pct(att?.attempt_points, att?.attempt_max_points), [att?.attempt_points, att?.attempt_max_points]);
    const verInfo = useMemo(()=> verificationText(att?.verification_status), [att?.verification_status]);

    async function confirmCancel(){
        const text = window.prompt('Aby unieważnić podejście wpisz: tak');
        if (text?.trim().toLowerCase() !== 'tak') return;
        try{
            const r = await fetch(`/api/prowadzacy/attempt/${id}/cancel`, {
                method:'POST', credentials:'include', headers: { ...authHeader() }
            });
            if (!r.ok){
                const j = await r.json().catch(()=> ({}));
                throw new Error(j?.blad || `Błąd unieważniania (${r.status})`);
            }
            await fetchData();
            alert('Podejście zostało unieważnione.');
        } catch(e){
            alert(e.message || 'Błąd unieważniania');
        }
    }

    async function sendDecision(decision){
        try{
            const r = await fetch(`/api/prowadzacy/attempt/${id}/verification`, {
                method:'PATCH',
                credentials:'include',
                headers: { 'Content-Type':'application/json', ...authHeader() },
                body: JSON.stringify({ decision })
            });
            if (!r.ok){
                const j = await r.json().catch(()=> ({}));
                throw new Error(j?.blad || `Błąd weryfikacji (${r.status})`);
            }
            setVerifyOpen(false);
            await fetchData();
        }catch(e){ alert(e.message || 'Błąd'); }
    }

    async function completeGrading(){
        const ok = window.confirm('Zatwierdzić ocenę i zakończyć ocenianie?');
        if (!ok) return;
        try{
            const r = await fetch(`/api/prowadzacy/attempt/${id}/complete`, {
                method:'POST', credentials:'include', headers: { ...authHeader() }
            });
            if (!r.ok){
                const j = await r.json().catch(()=> ({}));
                throw new Error(j?.blad || `Błąd zatwierdzania (${r.status})`);
            }
            await fetchData();
            alert('Ocenianie zatwierdzone.');
        }catch(e){ alert(e.message || 'Błąd'); }
    }

    const goBackToTerm = () => {
        const termId = att?.term_id;
        if (!termId) return;
        navigate(`/prowadzacy/termin/${termId}/przebieg`);
    };

    async function addExtraTime(){
        setExtraErr(''); setExtraOk('');
        const minutes = Number(String(extraMin).replace(',', '.'));
        if (!Number.isFinite(minutes) || minutes <= 0) {
            setExtraErr('Podaj dodatnią liczbę minut.');
            return;
        }
        try{
            const r = await fetch(`/api/exam/attempt/${id}/extra-time`, {
                method:'POST',
                credentials:'include',
                headers: { 'Content-Type':'application/json', ...authHeader() },
                body: JSON.stringify({ minutes })
            });
            if (!r.ok){
                const j = await r.json().catch(()=> ({}));
                throw new Error(j?.blad || `Błąd dodawania czasu (${r.status})`);
            }
            await r.json();
            setExtraOk('Czas został dodany.');
            setExtraMin('');
            await fetchData();
        }catch(e){
            setExtraErr(e.message || 'Błąd dodawania czasu');
        }
    }

    async function saveAttemptNote(){
        setSavingAttNote(true); setAttNoteMsg('');
        try{
            const r = await fetch(`/api/prowadzacy/attempt/${id}/note`, {
                method:'PATCH',
                credentials:'include',
                headers: { 'Content-Type':'application/json', ...authHeader() },
                body: JSON.stringify({ attempt_note: attNoteVal })
            });
            if (!r.ok){
                const j = await r.json().catch(()=> ({}));
                throw new Error(j?.blad || `Błąd zapisu notatki (${r.status})`);
            }
            setAttNoteMsg('Zapisano notatkę.');
            await fetchData();
        }catch(e){
            setAttNoteMsg(e.message || 'Błąd zapisu notatki');
        }finally{
            setSavingAttNote(false);
        }
    }

    const showExtraTime = ['in_progress','submitted', 'awaiting_manual','auto_graded' ].includes(att?.status);

    return (
        <div style={{ padding: '0 16px', marginTop:80, marginBottom:30 }}>
            <h1 style={{ marginBottom:5 }}>Szczegóły podejścia</h1>
            <div style={{ marginBottom:12 }}>
                <button
                    className={'but-smal'}
                    onClick={goBackToTerm}
                    disabled={!att?.term_id}
                    title={att?.term_id ? 'Wróć do listy uczestników' : 'Brak powiązanego terminu'}
                    style={{ cursor: att?.term_id ? 'pointer' : 'not-allowed', opacity: att?.term_id ? 1 : .6 }}
                >
                    Powrót do wszystkich
                </button>
            </div>

            {loading ? (
                <div>Ładowanie…</div>
            ) : err ? (
                <div style={{ color:'crimson' }}>{err}</div>
            ) : !att ? (
                <div>Brak danych.</div>
            ) : (
                <div style={{ display:'grid', gap:16 }}>
                    {/* 1. Dane */}
                    <section style={card}>
                        <h3 style={{ marginTop:0 }}>Dane</h3>
                        <div style={row}><div style={k}>Imię</div><div style={v}>{usr?.first_name || '—'}</div></div>
                        <div style={row}><div style={k}>Nazwisko</div><div style={v}>{usr?.last_name || '—'}</div></div>
                        <div style={row}><div style={k}>Album</div><div style={v}><code>{usr?.indeks || '—'}</code></div></div>
                        <div style={row}><div style={k}>Czas rozpoczęcia</div><div style={v}>{prettyDT(att?.started_at)}</div></div>
                        <div style={row}><div style={k}>Czas zakończenia</div><div style={v}>{prettyDT(att?.finished_at)}</div></div>
                        <div style={row}><div style={k}>Tryb chroniony</div><div style={v}>{clientBadge(att?.active_client, att?.active_client_message)}</div></div>
                    </section>

                    {/* 1.b Status + Punkty */}
                    <section style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                        <div style={card}>
                            <h3 style={{ marginTop:0 }}>Status</h3>
                            <div style={{ fontSize:18, fontWeight:700 }}>{statusLabel(att?.status)}</div>
                            {att?.attempt_guest ? <div style={{ marginTop:6, fontSize:13, color:'#6b7280' }}>(gość — dołączył kodem)</div> : null}
                        </div>
                        <div style={card}>
                            <h3 style={{ marginTop:0 }}>Punkty</h3>
                            <div style={{ fontSize:18, fontWeight:700 }}>
                                {att.attempt_points}
                                {' '}/{' '}
                                {att.attempt_max_points}
                                {pointsPct!=null ? <span style={{ marginLeft:8, color:'#6b7280' }}>({pointsPct}%)</span> : null}
                            </div>
                        </div>
                    </section>

                    {/* 1.c Dodaj czas */}
                    {showExtraTime && (
                        <section style={card}>
                            <h3 style={{ marginTop:0 }}>Dodaj czas</h3>
                            <div style={{ color:'#6b7280', marginBottom:8 }}>
                                Przyznaj studentowi dodatkowy czas na rozwiązanie egzaminu.
                            </div>
                            <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                                <label style={{ display:'flex', alignItems:'center', gap:8 }}>
                                    <span>Podaj liczbę minut:</span>
                                    <input
                                        type="number"
                                        min="1"
                                        step="1"
                                        value={extraMin}
                                        onChange={e=>setExtraMin(e.target.value)}
                                        style={{ width:120, padding:'8px', border:'1px solid #e5e7eb', borderRadius:8 }}
                                    />
                                </label>
                                <button className={'but-smal'}
                                        onClick={addExtraTime}
                                        style={{ padding:'8px 12px', boxShadow:'none'  }}
                                >
                                    Dodaj
                                </button>
                                {extraOk && <span style={{ color:'#059669', marginLeft:8 }}>{extraOk}</span>}
                                {extraErr && <span style={{ color:'#dc2626', marginLeft:8 }}>{extraErr}</span>}
                            </div>
                        </section>
                    )}

                    {/* 1.d Notatka indywidualna */}
                    <section style={card}>
                        <h3 style={{ marginTop:0 }}>Notatka dla studenta (indywidualna)</h3>
                        <textarea
                            rows={3}
                            value={attNoteVal}
                            onChange={e=>setAttNoteVal(e.target.value)}
                            style={{ width:'100%', border:'1px solid #e5e7eb', borderRadius:8, padding:8 }}
                        />
                        <div style={{ marginTop:8, display:'flex', gap:8, alignItems:'center' }}>
                            <button className={'but-smal'} onClick={saveAttemptNote} disabled={savingAttNote}>
                                {savingAttNote ? 'Zapisywanie…' : 'Zapisz notatkę'}
                            </button>
                            {attNoteMsg && <span style={{ color: attNoteMsg.includes('Błąd') ? '#dc2626' : '#059669' }}>{attNoteMsg}</span>}
                        </div>
                    </section>

                    {/* 2. Weryfikacja */}
                    <section style={card}>
                        <h3 style={{ marginTop:0 }}>Weryfikacja</h3>
                        <div style={{ color: verInfo.color, fontWeight:600, marginBottom:10 }}>{verInfo.text}</div>

                        {['manual_pending','manual_rejected'].includes(att?.verification_status) && (
                            <>
                                <button
                                    className={'but-smal'}
                                    onClick={()=>setVerifyOpen(o=>!o)}
                                    aria-expanded={verifyOpen}
                                >
                                    Weryfikuj
                                </button>

                                {verifyOpen && (
                                    <>
                                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginTop:12 }}>
                                            <div>
                                                <div style={{ fontWeight:600, marginBottom:6 }}>Zdjęcie z profilu</div>
                                                {ver?.face_url
                                                    ? <img src={ver.face_url} alt="Profil" style={{ maxWidth:'100%', borderRadius:8, border:'1px solid #eee' }} />
                                                    : <div style={{ opacity:.7 }}>Brak</div>}
                                            </div>
                                            <div>
                                                <div style={{ fontWeight:600, marginBottom:6 }}>Zdjęcie z weryfikacji</div>
                                                {ver?.evidence_url
                                                    ? <img src={ver.evidence_url} alt="Weryfikacja" style={{ maxWidth:'100%', borderRadius:8, border:'1px solid #eee' }} />
                                                    : <div style={{ opacity:.7 }}>Brak</div>}
                                            </div>
                                        </div>

                                        <div style={{ display:'flex', gap:8, marginTop:12 }}>
                                            <button
                                                className={'but-smal'}
                                                onClick={()=>sendDecision('approved')}
                                                style={{ background:'#10b981', color:'#fff'  }}
                                            >
                                                Akceptuj
                                            </button>
                                            <button
                                                className={'but-smal'}
                                                onClick={()=>sendDecision('rejected')}
                                                style={{ background:'#ef4444', color:'#fff' }}
                                            >
                                                Niezgodne
                                            </button>
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        {['auto_pass','manual_approved','not_required'].includes(att?.verification_status) && (
                            <div style={{ opacity:.8, marginTop:8 }}>Dodatkowe działania nie są wymagane.</div>
                        )}
                    </section>

                    {/* 3. Proctoring */}
                    <section style={card}>
                        <h3 style={{ marginTop:0 }}>Proctoring</h3>
                        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                            <span
                                title={`Punkty karne: ${att?.attempt_flags ?? '—'}`}
                                style={{
                                    width:22, height:22, borderRadius:'50%',
                                    background: proctorColor(att?.attempt_flags),
                                    boxShadow:'0 0 0 1px rgba(0,0,0,.05)', display:'inline-block'
                                }}
                            />
                            <div>Otrzymano <b>{Number.isFinite(att?.attempt_flags) ? att.attempt_flags : 0}</b> punktów karnych.</div>
                        </div>

                        <div style={{ display:'flex', gap:8, marginTop:12 }}>
                            <button
                                className={'but-smal'}
                                onClick={()=> navigate(att?.proctoring ? `/prowadzacy/attempt/${id}/player` : `/prowadzacy/attempt/${id}/flagi`)}
                            >
                                {att?.proctoring ? 'Zobacz nagranie i flagi' : 'Zobacz flagi'}
                            </button>

                            <button
                                className={'but-smal'}
                                onClick={confirmCancel}
                                style={{ background:'#fee2e2', color:'#b91c1c' }}
                            >
                                Wstrzymaj egzamin / Unieważnij podejście
                            </button>
                        </div>
                    </section>

                    {/* 4. Ocenianie */}
                    <section style={card}>
                        <h3 style={{ marginTop:0}}>Ocenianie</h3>

                        {['in_progress','submitted','awaiting_manual'].includes(att?.status) && (
                            <button
                                className={'but-smal'}
                                onClick={()=> navigate(`/prowadzacy/attempt/${id}/ocena`)}
                                style={{ marginTop:10 }}
                            >
                                Przejdź do oceniania
                            </button>
                        )}

                        {att?.status === 'auto_graded' && (
                            <div style={{ display:'flex', gap:8 }}>
                                <button
                                    className={'but-smal'}
                                    onClick={()=> navigate(`/prowadzacy/attempt/${id}/ocena`)}
                                >
                                    Podejrzyj odpowiedzi
                                </button>
                                <button
                                    className={'but-smal'}
                                    onClick={completeGrading}
                                >
                                    Zatwierdź
                                </button>
                            </div>
                        )}

                        {att?.status === 'completed' && (
                            <div style={{ display:'grid', gap:8 }}>
                                <div style={{ color:'#059669', fontWeight:600 }}>Ocenianie zakończone.</div>
                                <div style={{ display:'flex', gap:8 }}>
                                    <button
                                        className={'but-smal'}
                                        onClick={()=> navigate(`/prowadzacy/attempt/${id}/ocena`)}
                                        style={{ padding:'8px 12px' }}
                                    >
                                        Edytuj ocenianie
                                    </button>

                                </div>
                            </div>
                        )}
                    </section>
                </div>
            )}
        </div>
    );
}
